import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api, wsUrl } from "../lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const socketRef = useRef(null);
  const listenersRef = useRef(new Set());
  const retryRef = useRef(null);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("gcl_token");
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await api.me();
      setUser(data);
    } catch {
      localStorage.removeItem("gcl_token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    loadMe();
  }, [loadMe]);

  const connectWs = useCallback(() => {
    const token = localStorage.getItem("gcl_token");
    if (!token || socketRef.current) return;
    const ws = new WebSocket(wsUrl(token));
    socketRef.current = ws;
    ws.onopen = () => setWsConnected(true);
    ws.onmessage = (e) => {
      let msg; try { msg = JSON.parse(e.data); } catch { return; }
      listenersRef.current.forEach((fn) => fn(msg));
    };
    ws.onclose = () => {
      setWsConnected(false);
      socketRef.current = null;
      if (localStorage.getItem("gcl_token")) {
        retryRef.current = setTimeout(connectWs, 2000);
      }
    };
    ws.onerror = () => { try { ws.close(); } catch {} };
  }, []);

  useEffect(() => {
    if (user) connectWs();
    return () => { if (retryRef.current) clearTimeout(retryRef.current); };
  }, [user, connectWs]);

  const subscribe = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  const sendWs = useCallback((obj) => {
    const ws = socketRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(obj));
  }, []);

  const setSession = async (token, userData) => {
    localStorage.setItem("gcl_token", token);
    setUser(userData);
  };

  const login = async (email, password) => {
    const { data } = await api.login(email, password);
    await setSession(data.token, data.user);
    return data.user;
  };
  const register = async (payload) => {
    const { data } = await api.register(payload);
    await setSession(data.token, data.user);
    return data.user;
  };
  const logout = async () => {
    try { await api.logout(); } catch {}
    localStorage.removeItem("gcl_token");
    if (socketRef.current) { try { socketRef.current.close(); } catch {} socketRef.current = null; }
    setUser(null);
  };
  const refreshUser = async () => {
    const { data } = await api.me();
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setSession, refreshUser, subscribe, sendWs, wsConnected }}>
      {children}
    </AuthContext.Provider>
  );
}
